import { Injectable } from '@angular/core';
import { QueryEntity, QueryConfig, Order } from '@datorama/akita';
import { Observable } from 'rxjs';
import { RewardsWalletState, RewardsWalletStore } from './rewards-wallet.store';
import { RewardsWalletItem } from './models/rewards-wallet.model';
import moment from 'moment/moment';
import { map, tap } from 'rxjs/operators';
import { WalletGroupItem } from './models/rewards-wallet-group.model';
import { DateTime } from 'luxon';
import { MartketplaceTransferredItem } from '../marketplace/models/marketplace-transferred-item.model';

@Injectable({ providedIn: 'root' })
@QueryConfig({ sortBy: 'EndDate', sortByOrder: Order.ASC })
export class RewardsWalletQuery extends QueryEntity<RewardsWalletState> {
  constructor(protected store: RewardsWalletStore) {
    super(store);
  }

  /** All items. */
  selectRewardWalletItems(showExpired: boolean = false): Observable<RewardsWalletItem[]> {
    return this.selectAll();
  }

  /** get marketplace transferred items */
  selectMarketplaceTransferredItems(): Observable<MartketplaceTransferredItem[] | null> {
    return this.select((state) => state.transferredItems);
  }

  selectUnredeemedRewardWalletItems(): Observable<RewardsWalletItem[]> {
    const utcNow = DateTime.utc();

    return this.selectAll({
      filterBy: (entity) => {
        const isRedeemed = !!entity.RedeemDate;
        const isExpired = DateTime.fromISO(entity.ExpireDate as string, { zone: 'utc' }) < utcNow;

        return !isRedeemed && !isExpired;
      },
    }).pipe(map((items) => items.sort(this._sortByPurchaseDate())));
  }

  /** All items by redeem date. */
  selectRewardWalletItemsHistory(): Observable<RewardsWalletItem[]> {
    const utcNow = DateTime.utc();

    this.selectAll().subscribe(console.log);

    return this.selectAll({
      filterBy: (entity) => {
        const isRedeemed = !!entity.RedeemDate;
        const isExpired = DateTime.fromISO(entity.ExpireDate as string, { zone: 'utc' }) < utcNow;

        return isRedeemed || isExpired;
      },
    }).pipe(
      map((items) => {
        return items.sort(this._sortByRedeemDate());
      }),
      tap((i) => console.log(i, `post sort`))
    );
  }

  /**
   * @deprecated
   */
  selectGroupedRewardWalletItems(): Observable<WalletGroupItem[]> {
    return this.selectRewardWalletItems().pipe(map((items) => this._groupItems(items)));
  }

  selectGroupedUnredeemedWalletItems(): Observable<WalletGroupItem[]> {
    return this.selectUnredeemedRewardWalletItems().pipe(map((items) => this._groupItems(items)));
  }

  /**
   * Select the group items excluding expired
   */
  selectItemsByGroupId(groupId: string | undefined): Observable<WalletGroupItem | undefined> {
    return this.selectGroupedUnredeemedWalletItems().pipe(map((groups) => groups.find((group) => group.groupId === groupId)));
  }

  private _getUniqueValues(value: any, index: number, arr: any[]) {
    return arr.indexOf(value) === index;
  }

  private _groupItems(items: any[]): WalletGroupItem[] {
    const uniqueIds = items.map((item) => item.GroupId).filter(this._getUniqueValues);
    return uniqueIds.map((id) => {
      const groupedItems = items.filter((item) => item.GroupId === id);
      const isMulti = groupedItems.length > 1;
      const count = groupedItems.length;
      return { groupId: id, items: groupedItems, multi: isMulti, count };
    });
  }

  /** A specific item by ID. */
  selectRewardWalletItem(id: string): Observable<RewardsWalletItem | undefined> {
    return this.selectAll().pipe(map((all) => all.find((item) => item.WalletId === id)));
  }

  private _sortByPurchaseDate() {
    return (a: RewardsWalletItem, b: RewardsWalletItem) => moment.utc(b.PurchaseDate).diff(moment.utc(a.PurchaseDate));
  }

  private _sortByRedeemDate() {
    return (a: RewardsWalletItem, b: RewardsWalletItem) => {
      const aDate = a.RedeemDate ? DateTime.fromISO(a.RedeemDate as string, { zone: 'utc' }) : null;
      const bDate = b.RedeemDate ? DateTime.fromISO(b.RedeemDate as string, { zone: 'utc' }) : null;

      if (!aDate && !bDate) return 0;
      if (!aDate) return 1; // a goes after b
      if (!bDate) return -1; // b goes after a

      return bDate.toMillis() - aDate.toMillis();
    };
  }
}
